import React, { useContext, useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaHeart, FaLightbulb, FaMinus, FaPlus, FaRegCheckCircle } from "react-icons/fa";
import { addToCart } from "../../../redux/orebiSlice";
import { formatPrice } from "../../../constants/funciones";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useFavorites } from "../../../context/FavoritesContext";
import { UserConfigContext } from "../../../context/UserConfigContext";
import { getDiscountedUnitPrice, getProductBasePrice, getQuantityDiscount } from "../../../constants/cartDiscounts";
import PriceConsultationButton from "../../common/PriceConsultationButton";

const ProductInfo = ({ productInfo }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cartItems = useSelector((state) => state.orebiReducer.products || []);
  const {
    ensureFavoritesLoaded,
    isFavorite: isFavoriteInContext,
    toggleFavorite: toggleFavoriteInContext,
  } = useFavorites();
  const { config1, configEcommerce } = useContext(UserConfigContext);

  const [quantity, setQuantity] = useState(1);
  const [isFavorite, setIsFavorite] = useState(false);
  const [userData] = useState(() => {
    const data = localStorage.getItem("user");
    return data ? JSON.parse(data) : {};
  });

  const lastProductCode = useRef(null);

  function normalizeQuantity(value) {
    const parsed = parseInt(value, 10);
    if (Number.isNaN(parsed) || parsed < 1) return 1;
    return parsed;
  }

  const userType = userData?.data?.empresas?.[0]?.tipo || "";
  const stock = useMemo(() => Number(productInfo?.Stock) || 0, [productInfo?.Stock]);
  const clampQuantityToStock = (value) => {
    const quantity = normalizeQuantity(value);
    return stock > 0 ? Math.min(quantity, stock) : 1;
  };
  const productCode = useMemo(() => productInfo?.codigo || "", [productInfo?.codigo]);
  const productDesc = useMemo(() => productInfo?.Descripcion || "", [productInfo?.Descripcion]);
  const hasOffer = Number(productInfo?.PrecioOferta) > 0;
  const hasHiddenPrice = configEcommerce?.ocultar_precio || Number(productInfo?.ocultar_precio) === 1;
  const hasHiddenStock = configEcommerce?.ocultar_stock || Number(productInfo?.ocultar_stock) === 1;
  const contactEmailsSource = config1?.config_ecommerce?.emails_contactos || configEcommerce?.emails_contactos || [];
  const contactEmails = (Array.isArray(contactEmailsSource) ? contactEmailsSource : String(contactEmailsSource).split(/[;,]/))
    .map((email) => typeof email === "string" ? email.trim() : email?.email || email?.mail || email?.correo)
    .filter(Boolean);
  const currentQuantity = clampQuantityToStock(quantity);
  const basePrice = useMemo(() => getProductBasePrice(productInfo), [productInfo]);
  const quantityDiscount = useMemo(
    () => getQuantityDiscount(productInfo, currentQuantity),
    [productInfo, currentQuantity]
  );
  const finalPrice = useMemo(
    () => getDiscountedUnitPrice(productInfo, currentQuantity),
    [productInfo, currentQuantity]
  );
  const hasQuantityDiscount = quantityDiscount.porcentaje > 0;
  const ivaLabel = productInfo?.iva ?? 0;
  const showStockMessage = Boolean(configEcommerce?.con_sin_stock && productInfo?.stock_mensaje);
  const stockMessageStatus = useMemo(() => {
    const message = String(productInfo?.stock_mensaje || "").toLowerCase();
    if (message.includes("agotado") || message.includes("sin stock")) return "danger";
    if (message.includes("bajo") || message.includes("poco") || message.includes("limitado")) return "warning";
    return "success";
  }, [productInfo?.stock_mensaje]);
  const stockMessageClass = {
    danger: "text-red-600",
    warning: "text-amber-500",
    success: "text-emerald-600",
  }[stockMessageStatus];

  useEffect(() => {
    if (!productInfo?.codigo) return;

    const cartItem = cartItems.find((item) => item.codigo === productInfo.codigo);
    const newQty = cartItem ? clampQuantityToStock(cartItem.quantity) : 1;

    setQuantity((prevQuantity) => (prevQuantity !== newQty ? newQty : prevQuantity));
  }, [cartItems, productInfo?.codigo]);

  useEffect(() => {
    const userId = userData?.data?.user?.id;
    const codigo = productInfo?.codigo;

    if (!userId || !codigo) return;
    if (lastProductCode.current === codigo) return;

    lastProductCode.current = codigo;

    const fetchFavorites = async () => {
      try {
        const favorites = await ensureFavoritesLoaded();
        const isFav =
          isFavoriteInContext(codigo) ||
          favorites.some((fav) => fav.codigo === codigo);
        setIsFavorite(isFav);
      } catch (err) {
        console.warn("Error cargando favoritos:", err);
      }
    };

    fetchFavorites();
  }, [ensureFavoritesLoaded, isFavoriteInContext, userData?.data?.user?.id, productInfo?.codigo]);

  const handleQuantityChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setQuantity(clampQuantityToStock(value));
    }
  };

  const changeQuantity = (delta) => {
    const nextValue = clampQuantityToStock(normalizeQuantity(quantity) + delta);
    setQuantity(nextValue);
  };

  const handleAddToCart = () => {
    if (!productInfo) return;

    if (stock <= 0) {
      Swal.fire("Sin stock", "No hay unidades disponibles para agregar al carrito.", "info");
      return;
    }

    if (hasHiddenPrice) {
      Swal.fire({
        icon: "info",
        title: "Precio a consultar",
        text: contactEmails.length
          ? `Comuníquese con nosotros a: ${contactEmails.join(", ")}`
          : "Comuníquese con nosotros para consultar el precio.",
        confirmButtonText: "Entendido",
      });
      return;
    }

    const qtyToAdd = clampQuantityToStock(quantity);
    const discount = getQuantityDiscount(productInfo, qtyToAdd);
    const priceWithDiscount = getDiscountedUnitPrice(productInfo, qtyToAdd);

    dispatch(addToCart({
      ...productInfo,
      quantity: qtyToAdd,
      precioOriginal: getProductBasePrice(productInfo),
      precioConDescuento: priceWithDiscount,
      descuentoAplicado: discount.porcentaje,
    }));
    Swal.fire("Producto Añadido", "", "success");
  };

  const toggleFavorite = async () => {
    if (!userData?.data?.user) {
      navigate("/signin");
      return;
    }

    try {
      const updated = await toggleFavoriteInContext(productInfo);
      if (updated) {
        setIsFavorite(!isFavorite);
      }
    } catch (err) {
      Swal.fire("Error", "No se pudo actualizar favoritos.", "error");
    }
  };

  if (!productInfo) return <div>Cargando...</div>;

  return (
    <section className="rounded-[32px] border border-gray-200 bg-white p-6 shadow-sm md:p-7">
      <div className="flex flex-col gap-6">
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
              Codigo {productCode}
            </span>
            {hasOffer && (
              <span className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-rose-600">
                Oferta
              </span>
            )}
            {hasQuantityDiscount && (
              <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-600">
                {quantityDiscount.porcentaje}% por cantidad
              </span>
            )}
          </div>

          <div>
            <h1 className="text-3xl font-bold leading-tight text-gray-900">{productDesc}</h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-gray-500">
              Revisa disponibilidad, ajuste la cantidad y agregalo al carrito desde esta misma pantalla.
            </p>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-gray-500">
              Tipo de Producto: {productInfo.tipo}
            </p>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-gray-500">
              Grupo de Producto: {productInfo.grupo}
            </p>            
          </div>          
        </div>

        <div className="rounded-[28px] border border-gray-200 bg-gradient-to-br from-slate-50 via-white to-slate-100 p-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">Precio</p>
              <div className="mt-2 flex flex-wrap items-end gap-3">
                {!hasHiddenPrice && hasOffer && (
                  <span className="text-lg text-gray-400 line-through">
                    {productInfo.simbolo}
                    {formatPrice(productInfo.PrecioLista)}
                  </span>
                )}
                {!hasHiddenPrice && hasQuantityDiscount && (
                  <span className="text-lg text-gray-400 line-through">
                    {productInfo.simbolo}
                    {formatPrice(basePrice)}
                  </span>
                )}
                {hasHiddenPrice ? (
                  <PriceConsultationButton className="text-lg font-semibold text-amber-700 underline decoration-dotted underline-offset-2">
                    {productInfo.ocultar_precio_texto || "Precio a consultar"}
                  </PriceConsultationButton>
                ) : (
                  <span className="text-4xl font-bold text-primeColor">
                    {productInfo.simbolo}
                    {formatPrice(finalPrice || 0)}
                  </span>
                )}
              </div>
              {!hasHiddenPrice && hasQuantityDiscount && (
                <p className="mt-2 text-sm font-semibold text-emerald-700">
                  Descuento aplicado por {currentQuantity} unidades: {quantityDiscount.porcentaje}%
                </p>
              )}
            </div>

            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
              * El precio no incluye impuestos (IVA: {ivaLabel})
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-gray-200 bg-slate-50 px-4 py-4">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">Disponibilidad</p>
            <div className="mt-3 flex items-center gap-3">
              {!showStockMessage && !hasHiddenStock ? (
              <span
                className={`inline-flex h-12 w-12 items-center justify-center rounded-full border-2 text-lg font-bold ${
                  stock > 0 ? "border-emerald-300 text-emerald-600" : "border-gray-300 text-gray-400"
                }`}
              >
                {stock}
              </span>
              ) : ''}
              <div>
                <p className="font-semibold text-gray-800">{hasHiddenStock ? "Stock a consultar" : stock > 0 ? "Stock" : "Sin stock"}</p>
                {hasHiddenStock ? (
                  <PriceConsultationButton
                    className="text-sm font-semibold text-amber-700 underline decoration-dotted underline-offset-2"
                    modalTitle="Stock a consultar"
                  >
                    {productInfo.ocultar_stock_texto || "Consultar disponibilidad"}
                  </PriceConsultationButton>
                ) : showStockMessage ? (
                  <p className={`inline-flex items-center gap-2 text-sm font-semibold ${stockMessageClass}`}>
                    <FaLightbulb className="shrink-0" />
                    <span>{productInfo.stock_mensaje}</span>
                  </p>
                ) : (
                  <p className="text-sm text-gray-500">
                    {stock > 0 ? "Disponible para agregar al carrito" : "Consultanos por reposicion"}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-slate-50 px-4 py-4">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">Tipo de precio</p>
            <div className="mt-3 flex items-center gap-3 text-gray-700">
              <FaRegCheckCircle className="text-emerald-500" />
              <span className="font-medium">{hasOffer ? "Precio promocional activo" : "Precio de lista vigente"}</span>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-slate-50 px-4 py-4">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">Favoritos</p>
            <button
              type="button"
              onClick={toggleFavorite}
              className={`mt-3 inline-flex items-center gap-3 rounded-full px-4 py-2 text-sm font-semibold transition ${
                isFavorite
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50"
              }`}
            >
              <FaHeart />
              {isFavorite ? "Guardado en favoritos" : "Agregar a favoritos"}
            </button>
          </div>
        </div>

        <div className="rounded-[28px] border border-gray-200 bg-white p-5">
          <div className="flex flex-col gap-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">Cantidad</p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <div className="inline-flex items-center rounded-2xl border border-gray-200 bg-slate-50 p-1">
                  <button
                    type="button"
                    onClick={() => changeQuantity(-1)}
                    disabled={stock <= 0 || currentQuantity <= 1}
                    className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-600 transition hover:bg-white"
                  >
                    <FaMinus className="text-xs" />
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    min="1"
                    max={Math.max(1, stock)}
                    onChange={handleQuantityChange}
                    onBlur={() => setQuantity(clampQuantityToStock(quantity))}
                    disabled={stock <= 0}
                    className="h-10 w-16 border-0 bg-transparent text-center text-base font-semibold text-gray-800 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => changeQuantity(1)}
                    disabled={stock <= 0 || currentQuantity >= stock}
                    className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-600 transition hover:bg-white"
                  >
                    <FaPlus className="text-xs" />
                  </button>
                </div>

                <button
                  onClick={handleAddToCart}
                  disabled={stock <= 0}
                  className="inline-flex min-h-[48px] items-center justify-center rounded-full bg-gray-900 px-6 text-sm font-semibold text-white transition hover:bg-black"
                >
                  Añadir producto
                </button>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                {hasHiddenPrice ? (
                  <PriceConsultationButton className="font-semibold text-amber-700 underline decoration-dotted underline-offset-2">
                    Precio a consultar
                  </PriceConsultationButton>
                ) : (
                  <span className="font-semibold text-gray-800">
                    {`Total: ${productInfo.simbolo}${formatPrice(finalPrice * currentQuantity)}`}
                  </span>
                )}
                {!hasHiddenPrice && hasQuantityDiscount && (
                  <span className="rounded-full bg-emerald-50 px-3 py-1 font-semibold text-emerald-700">
                    Ahorro: {productInfo.simbolo}{formatPrice((basePrice - finalPrice) * currentQuantity)}
                  </span>
                )}
              </div>
            </div>

            {(userType === "u" || userType === "a" || userType === "s" || userType === "o" || userType === "v") && (
              <p className="text-sm text-gray-500">
                El producto se agrega con la cantidad indicada y mantiene el precio visible al momento de la consulta.
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductInfo;
